import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "_shared/_stores/root-reducer";
import Guid from "_shared/guids/guid";
import { userNetworkInvitationApi } from "users/networking/connections/invitations/_api/network-invitation-api";
import type { NetworkInvitationInfoJSON } from "users/networking/connections/invitations/_schemas/network-invitation-info-schema";
import NetworkInvitationInfo from "users/networking/connections/invitations/network-invitation-info";

export type NetworkInvitationRemoval = {
  invitationId?: string;
};

export type NetworkInvitationsStoreState = {
  allIds: string[];
  byId: {
    entries: Record<string, NetworkInvitationInfoJSON>;
  };
};

const initialState: NetworkInvitationsStoreState = {
  allIds: [],
  byId: {
    entries: {},
  },
};

function upsertNetworkInvitationJSON(
  state: NetworkInvitationsStoreState,
  invitation: NetworkInvitationInfoJSON,
  options: { addToFront?: boolean } = {},
): void {
  state.byId.entries[invitation.id] = invitation;
  if (state.allIds.includes(invitation.id)) return;

  if (options.addToFront) {
    state.allIds.unshift(invitation.id);
  } else {
    state.allIds.push(invitation.id);
  }
}

function removeNetworkInvitationJSON(
  state: NetworkInvitationsStoreState,
  removal: NetworkInvitationRemoval,
): void {
  if (!removal.invitationId) return;
  delete state.byId.entries[removal.invitationId];
  state.allIds = state.allIds.filter((id) => id !== removal.invitationId);
}

export const networkInvitationsSlice = createSlice({
  name: "networkInvitations",
  initialState,
  reducers: {
    upsertNetworkInvitation: (state, action: PayloadAction<NetworkInvitationInfoJSON>) => {
      upsertNetworkInvitationJSON(state, action.payload, { addToFront: true });
    },
    removeNetworkInvitation: (state, action: PayloadAction<NetworkInvitationRemoval>) => {
      removeNetworkInvitationJSON(state, action.payload);
    },
    clearNetworkInvitations: (state) => {
      state.allIds = [];
      state.byId.entries = {};
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(userNetworkInvitationApi.endpoints.getUserNetworkInvitations.matchFulfilled, (state, action) => {
      state.allIds = action.payload.data.map((invitation) => invitation.id);
      for (const invitation of action.payload.data) {
        upsertNetworkInvitationJSON(state, invitation);
      }
    });

    builder.addMatcher(userNetworkInvitationApi.endpoints.createNetworkInvitation.matchFulfilled, (state, action) => {
      upsertNetworkInvitationJSON(state, action.payload);
    });

    builder.addMatcher(userNetworkInvitationApi.endpoints.acceptNetworkInvitation.matchFulfilled, (state, action) => {
      removeNetworkInvitationJSON(state, {
        invitationId: action.meta.arg.originalArgs.invitationId.value,
      });
    });

    builder.addMatcher(userNetworkInvitationApi.endpoints.cancelNetworkInvitation.matchFulfilled, (state, action) => {
      removeNetworkInvitationJSON(state, {
        invitationId: action.meta.arg.originalArgs.invitationId.value,
      });
    });

    builder.addMatcher(userNetworkInvitationApi.endpoints.rejectNetworkInvitation.matchFulfilled, (state, action) => {
      removeNetworkInvitationJSON(state, {
        invitationId: action.meta.arg.originalArgs.invitationId.value,
      });
    });
  },
});

export const {
  clearNetworkInvitations,
  removeNetworkInvitation,
  upsertNetworkInvitation,
} = networkInvitationsSlice.actions;

const selectNetworkInvitations = createSelector(
  [(state: RootState) => state.networkInvitations.allIds, (state: RootState) => state.networkInvitations.byId.entries],
  (allIds, entries) => {
    const invitationJSON = allIds.length
      ? allIds.map((id) => entries[id]).filter((invitation): invitation is NetworkInvitationInfoJSON => !!invitation)
      : Object.values(entries);

    return NetworkInvitationInfo.fromJSONArray(invitationJSON);
  },
);

const selectNetworkInvitationById = createSelector(
  [(state: RootState) => state.networkInvitations.byId.entries, (_: RootState, invitationId?: Guid) => invitationId?.value],
  (entries, invitationIdValue) => {
    if (!invitationIdValue) return undefined;
    const invitationJSON = entries[invitationIdValue];
    if (!invitationJSON) return undefined;
    return NetworkInvitationInfo.fromJSON(invitationJSON);
  },
);

const selectNetworkInvitationByUserId = createSelector(
  [(state: RootState) => state.networkInvitations.byId.entries, (_: RootState, userId?: Guid) => userId?.value],
  (entries, userIdValue) => {
    if (!userIdValue) return undefined;
    // A pair keeps every invitation it ever exchanged: connecting, disconnecting and inviting again
    // leaves accepted ones beside the new open one. The open one decides where the pair stands;
    // without it, the latest does. Taking whichever came first showed "Add To Network" after a send.
    const invitations = Object.values(entries)
      .filter((invitation) => invitation.invitee.id === userIdValue || invitation.inviter.id === userIdValue)
      .sort((a, b) => Date.parse(b.created) - Date.parse(a.created));
    const invitationJSON = invitations.find((invitation) => invitation.status.toLowerCase() === "open") ?? invitations[0];
    if (!invitationJSON) return undefined;
    return NetworkInvitationInfo.fromJSON(invitationJSON);
  },
);

export const getNetworkInvitations = () => (state: RootState) => selectNetworkInvitations(state);

export const getNetworkInvitationById = (invitationId?: Guid) => (state: RootState) =>
  selectNetworkInvitationById(state, invitationId);

export const getNetworkInvitationByUserId = (userId?: Guid) => (state: RootState) =>
  selectNetworkInvitationByUserId(state, userId);
